import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import fieldService from '@/services/fieldService';
import { checkBookingSlots } from '@/services/bookingService';
import { openExternalUrl } from '@/utils/openExternalUrl';
import CalendarModal from '@/components/detail/CalendarModal';

// Helper to format Date to YYYY-MM-DD

// Helper to format Date to YYYY-MM-DD
const formatDateToYMD = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const formatOpeningHours = (openingHours: any): string => {
  if (!openingHours) return '06.00 - 23.00';
  if (typeof openingHours === 'string') return openingHours;
  if (typeof openingHours === 'object') {
    const dayKeys = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const today = dayKeys[new Date().getDay()];
    const val = openingHours[today] || openingHours.monday || openingHours.sunday || Object.values(openingHours)[0];
    if (typeof val === 'string' && val.trim()) return val;
  }
  return '06.00 - 23.00';
};

// Opening Hours helper component
const OpeningHoursComponent = ({ openingHours }: { openingHours: any }) => {
  const dayOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

  const dayMap: Record<string, string> = {
    monday: 'Senin',
    tuesday: 'Selasa',
    wednesday: 'Rabu',
    thursday: 'Kamis',
    friday: 'Jumat',
    saturday: 'Sabtu',
    sunday: 'Minggu',
  };

  if (!openingHours || typeof openingHours !== 'object') {
    return (
      <div className="bg-white rounded-3xl p-4 sm:p-5 border border-gray-100 shadow-2xs text-xs font-semibold text-gray-500">
        Buka setiap hari: 06.00 - 23.00
      </div>
    );
  }

  return (
    <div className="bg-white rounded-3xl p-4 sm:p-5 border border-gray-100 shadow-2xs">
      <ul className="divide-y divide-gray-100 text-xs font-medium text-gray-600">
        {dayOrder.map((dayKey) => {
          const jam = openingHours[dayKey];
          if (!jam) return null;
          return (
            <li key={dayKey} className="flex items-center justify-between py-2 first:pt-0 last:pb-0">
              <span className="font-bold text-[#0E1726]">{dayMap[dayKey]}</span>
              <span className="text-gray-500 font-semibold">{jam}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export const ClientFieldDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const [selectorBaseDate, setSelectorBaseDate] = useState<Date>(new Date());
  const [selectedDate, setSelectedDate] = useState<string>(formatDateToYMD(new Date()));
  const [isCalendarModalOpen, setIsCalendarModalOpen] = useState<boolean>(false);

  const handleDateSelect = (date: Date) => {
    const formattedDate = formatDateToYMD(date);
    const d1 = new Date(date);
    d1.setHours(0, 0, 0, 0);

    const currentBase = new Date(selectorBaseDate);
    currentBase.setHours(0, 0, 0, 0);

    const diffFromBase = Math.round((d1.getTime() - currentBase.getTime()) / (1000 * 60 * 60 * 24));

    if (diffFromBase < 0 || diffFromBase >= 3) {
      setSelectorBaseDate(d1);
    }

    setSelectedDate(formattedDate);
    setSelectedTimeSlots([]);
    setIsCalendarModalOpen(false);
  };

  const [arena, setArena] = useState<any | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Field selection state
  const [selectedFieldIndex, setSelectedFieldIndex] = useState<number>(0);

  // Selected time slots state
  const [selectedTimeSlots, setSelectedTimeSlots] = useState<string[]>([]);

  // Gallery active index
  const [activeImageIndex, setActiveImageIndex] = useState<number>(0);

  useEffect(() => {
    let isMounted = true;

    const fetchDetail = async () => {
      if (!slug) return;

      // Only set initial full-page loading if arena data isn't loaded yet
      if (!arena) {
        setInitialLoading(true);
      }
      setError(null);

      try {
        const data = await fieldService.getArenaDetail(slug, selectedDate);
        if (isMounted) {
          if (!data && !arena) {
            setError('Detail arena tidak ditemukan');
          } else if (data) {
            setArena(data);
          }
        }
      } catch (err: any) {
        if (isMounted && !arena) {
          // Fallback mock arena if API fails on initial load
          setArena({
            slug: slug,
            name: slug.replace(/-/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
            sport_categories: [{ name: 'Mini Soccer', icon: 'ph:soccer-ball-bold' }],
            opening_hours: '06.00 - 23.00',
            maps: { city: 'Kab. Sleman', address: 'Jl. Kaliurang Km 5, Sinduadi, Mlati, Kab. Sleman, DI Yogyakarta 55284' },
            images: {
              images: [
                'https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=800&auto=format&fit=crop&q=80',
                'https://images.unsplash.com/photo-1518604666860-9ed391f76460?w=800&auto=format&fit=crop&q=80',
                'https://images.unsplash.com/photo-1529900748604-07564a03e7a6?w=800&auto=format&fit=crop&q=80',
              ],
            },
            physical_fields: [
              {
                slug: 'lapangan-a',
                name: 'Lapangan A · Sintetis',
                type: 'Indoor · 30 x 50 m',
                price: 180000,
              },
              {
                slug: 'lapangan-b',
                name: 'Lapangan B · Vinyl',
                type: 'Indoor · 25 x 45 m',
                price: 160000,
              },
              {
                slug: 'lapangan-c',
                name: 'Lapangan C · Rumput',
                type: 'Outdoor · 30 x 50 m',
                price: 140000,
              },
            ],
          });
        }
      } finally {
        if (isMounted) {
          setInitialLoading(false);
        }
      }
    };

    fetchDetail();

    return () => {
      isMounted = false;
    };
  }, [slug, selectedDate]);

  // Reset selected slots when date or field changes
  useEffect(() => {
    setSelectedTimeSlots([]);
  }, [selectedDate, selectedFieldIndex]);

  if (initialLoading) {
    return (
      <div className="bg-[#F8FAFC] min-h-screen pt-4 px-4 max-w-md mx-auto space-y-4 animate-pulse">
        <div className="w-full h-48 rounded-2xl bg-gray-200" />
        <div className="bg-white rounded-3xl p-5 space-y-3">
          <div className="h-5 bg-gray-200 rounded-md w-2/3" />
          <div className="h-4 bg-gray-200 rounded-md w-1/3" />
        </div>
        <div className="bg-white rounded-3xl p-5 space-y-3">
          <div className="h-5 bg-gray-200 rounded-md w-1/2" />
          <div className="h-12 bg-gray-200 rounded-2xl w-full" />
        </div>
      </div>
    );
  }

  if (error || !arena) {
    return (
      <div className="bg-[#F8FAFC] min-h-screen flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl p-6 text-center max-w-sm w-full border border-gray-100 shadow-xs space-y-3">
          <Icon icon="ph:warning-circle-bold" className="w-10 h-10 text-red-500 mx-auto" />
          <h3 className="text-base font-extrabold text-[#0E1726]">Gagal Memuat Arena</h3>
          <p className="text-xs text-gray-500">{error || 'Data arena tidak ditemukan'}</p>
          <button
            onClick={() => navigate('/discovery/arena')}
            className="w-full py-2.5 rounded-xl bg-[#FC480D] text-white text-xs font-bold shadow-md hover:bg-[#D83B08] transition-colors"
          >
            Kembali ke Discovery
          </button>
        </div>
      </div>
    );
  }

  const galleryImages: string[] =
    arena.images?.images ||
    (Array.isArray(arena.images) ? arena.images : []) || [
      'https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=800&auto=format&fit=crop&q=80',
    ];

  // Fields list mapping
  const physicalFields = arena.physical_fields || [
    {
      slug: 'lapangan-a',
      name: 'Lapangan A · Sintetis',
      type: 'Indoor · 30 x 50 m',
      price: 180000,
    },
    {
      slug: 'lapangan-b',
      name: 'Lapangan B · Vinyl',
      type: 'Indoor · 25 x 45 m',
      price: 160000,
    },
    {
      slug: 'lapangan-c',
      name: 'Lapangan C · Rumput',
      type: 'Outdoor · 30 x 50 m',
      price: 140000,
    },
  ];

  const currentField = physicalFields[selectedFieldIndex] || physicalFields[0];

  // Helper to extract time slots array for a field
  const getFieldTimeSlots = (fieldObj: any) => {
    if (Array.isArray(fieldObj?.field_time_slot)) return fieldObj.field_time_slot;
    if (Array.isArray(fieldObj?.fields?.[0]?.field_time_slot)) return fieldObj.fields[0].field_time_slot;
    return [];
  };

  // Dynamically calculate unit price for current field from field_time_slot (final_price or base_price)
  const currentFieldSlots = getFieldTimeSlots(currentField);
  const firstPriceSlot = currentFieldSlots.find((s: any) => s.final_price || s.base_price);
  const fieldPrice =
    firstPriceSlot?.final_price ||
    firstPriceSlot?.base_price ||
    currentField?.price ||
    currentField?.fields?.[0]?.price ||
    180000;

  // Helper to convert date to day_of_week string based on selectedDate
  const getDayOfWeekFromDate = (dateStr: string) => {
    if (!dateStr) return 'monday';
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    const dayIndex = date.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const dayMap = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    return dayMap[dayIndex];
  };

  // Helper to filter time slots by selected day of week
  const getFilteredTimeSlots = (timeSlots: any[]) => {
    if (!Array.isArray(timeSlots)) return [];
    const selectedDayOfWeek = getDayOfWeekFromDate(selectedDate);
    return timeSlots.filter((slot) => slot.day_of_week === selectedDayOfWeek);
  };

  // Helper to get available slots count & array for a specific field on selectedDate
  const getAvailableSlotsForField = (fieldObj: any) => {
    const rawSlots = getFieldTimeSlots(fieldObj);
    const filteredSlots = getFilteredTimeSlots(rawSlots);
    const todayStr = formatDateToYMD(new Date());

    return filteredSlots.filter((slot: any) => {
      if (!slot || !slot.public_id) return false;
      const isSlotAvail =
        slot.is_available !== false &&
        slot.can_be_used !== false &&
        !(Array.isArray(slot.unavailable_date) && slot.unavailable_date.includes(selectedDate));
      if (!isSlotAvail) return false;

      // Filter out past time slots if selected date is today
      if (selectedDate === todayStr) {
        const now = new Date();
        if (slot.start_time) {
          const [startHour, startMinute] = slot.start_time.split(':').map(Number);
          const slotTime = new Date();
          slotTime.setHours(startHour, startMinute, 0, 0);
          if (slotTime < now) {
            return false;
          }
        }
      } else if (selectedDate < todayStr) {
        return false;
      }

      return true;
    });
  };

  const toggleTimeSlot = (time: string) => {
    if (selectedTimeSlots.includes(time)) {
      setSelectedTimeSlots(selectedTimeSlots.filter((t) => t !== time));
    } else {
      setSelectedTimeSlots([...selectedTimeSlots, time]);
    }
  };

  const getSlotObjectForTime = (timeStr: string) => {
    const apiSlots = getFilteredTimeSlots(getFieldTimeSlots(currentField));
    const formattedSearchTime = timeStr.replace('.', ':');
    const found = apiSlots.find(
      (s: any) =>
        s.start_time === timeStr ||
        s.start_time === formattedSearchTime ||
        s.start_time?.startsWith(formattedSearchTime)
    );

    const slotPrice = found?.final_price || found?.base_price || fieldPrice;

    if (found && found.public_id) {
      return {
        public_id: found.public_id,
        start_time: found.start_time || formattedSearchTime,
        end_time: found.end_time || `${parseInt(timeStr, 10) + 1}:00`,
        day_of_week: found.day_of_week || 'monday',
        duration_minutes: found.duration_minutes || 60,
        price: slotPrice,
        final_price: slotPrice,
        base_price: found.base_price || slotPrice,
      };
    }

    return {
      public_id: found?.public_id || `slot_${timeStr.replace('.', '_')}`,
      start_time: formattedSearchTime.length === 5 ? `${formattedSearchTime}:00` : formattedSearchTime,
      end_time: `${String(parseInt(timeStr, 10) + 1).padStart(2, '0')}:00:00`,
      day_of_week: 'monday',
      duration_minutes: 60,
      price: slotPrice,
      final_price: slotPrice,
      base_price: slotPrice,
    };
  };

  const selectedSlotObjects = selectedTimeSlots.map((time) => getSlotObjectForTime(time));
  const totalPrice = selectedSlotObjects.reduce((sum, slot) => {
    const p = slot.price || slot.final_price || slot.base_price || fieldPrice;
    return sum + (typeof p === 'number' ? p : parseInt(String(p), 10) || 0);
  }, 0);

  const isBookingReady = selectedTimeSlots.length > 0;

  const handleBooking = async () => {
    if (!isBookingReady) return;

    const slotObjects = selectedTimeSlots.map((time) => getSlotObjectForTime(time));

    try {
      const [isAvailable, bookingId] = await checkBookingSlots({
        arenaSlug: arena.slug,
        sessionId: `session_${Date.now()}`,
        selectedSlots: slotObjects,
        totalPrice: totalPrice,
        totalDuration: selectedTimeSlots.length,
        selectedDateIndex: 0,
        selectedDate: selectedDate,
        fieldData: {
          name: currentField?.name || 'Lapangan A · Sintetis',
          address: arena.maps?.address || '',
          slug: arena.slug,
        },
      });

      if (isAvailable) {
        navigate('/arena/payment', {
          state: {
            bookingId,
            arena: {
              slug: arena.slug,
              name: arena.name,
              address: arena.maps?.address,
              city: arena.maps?.city,
            },
            field: {
              slug: currentField?.slug || 'lapangan-a',
              name: currentField?.name || 'Lapangan A · Sintetis',
              price: fieldPrice,
            },
            date: selectedDate,
            dateLabel: dateLabel,
            timeSlots: selectedTimeSlots,
            selectedSlots: slotObjects,
            totalDuration: selectedTimeSlots.length,
            totalPrice: totalPrice,
          },
        });
      } else {
        alert('Beberapa slot yang Anda pilih sudah tidak tersedia. Silakan pilih slot lainnya.');
      }
    } catch (error) {
      console.error('Error checking slots availability:', error);
      // Fallback navigation if check API fails in dev
      navigate('/arena/payment', {
        state: {
          arena: {
            slug: arena.slug,
            name: arena.name,
            address: arena.maps?.address,
            city: arena.maps?.city,
          },
          field: {
            slug: currentField?.slug || 'lapangan-a',
            name: currentField?.name || 'Lapangan A · Sintetis',
            price: fieldPrice,
          },
          date: selectedDate,
          dateLabel: dateLabel,
          timeSlots: selectedTimeSlots,
          selectedSlots: slotObjects,
          totalDuration: selectedTimeSlots.length,
          totalPrice: totalPrice,
        },
      });
    }
  };

  const apiFacilities = Array.isArray(arena?.facilities?.data)
    ? arena.facilities.data
    : Array.isArray(arena?.facilities)
    ? arena.facilities
    : [];

  const displayFacilities =
    apiFacilities.length > 0
      ? apiFacilities.map((f: any) => ({
          icon: f.icon || 'ph:check-bold',
          label: f.name || f.label || 'Fasilitas',
        }))
      : [];

  const sportCategories = Array.isArray(arena?.sport_categories)
    ? arena.sport_categories
    : typeof arena?.sport_categories === 'string'
    ? [{ name: arena.sport_categories, icon: 'ph:soccer-ball-bold' }]
    : [{ name: 'Mini Soccer', icon: 'ph:soccer-ball-bold' }];

  const [selYear, selMonth, selDay] = (selectedDate || formatDateToYMD(new Date())).split('-').map(Number);
  const selectedDateObj = new Date(selYear, selMonth - 1, selDay);
  const monthNamesLabel = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Des'];
  const dateLabel = `${selectedDateObj.getDate()} ${monthNamesLabel[selectedDateObj.getMonth()]}`;
  const selectedFieldName = currentField?.name?.split('·')?.[0]?.trim() || currentField?.name || 'Lapangan A';

  return (
    <div className="bg-[#F8FAFC] min-h-screen pb-32 pt-4 px-4 max-w-md mx-auto space-y-5">
      {/* 1. HERO GALLERY BANNER */}
      <div className="relative w-full h-48 sm:h-52 rounded-3xl overflow-hidden bg-gray-100 shadow-2xs group">
        <img
          src={galleryImages[activeImageIndex] || galleryImages[0]}
          alt={arena.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />

        {/* Top-Left Badge */}
        <div className="absolute top-3 left-3">
          <span className="bg-white/95 backdrop-blur-xs text-[#FC480D] font-extrabold text-[11px] px-3 py-1 rounded-full flex items-center gap-1 shadow-xs border border-orange-100">
            <Icon icon="ph:fire-bold" className="w-3.5 h-3.5 text-[#FC480D]" />
            Cepat penuh
          </span>
        </div>

        {/* Bottom-Right Counter Badge */}
        {galleryImages.length > 1 && (
          <div className="absolute bottom-3 right-3">
            <span className="bg-black/60 backdrop-blur-xs text-white text-[11px] font-bold px-2.5 py-1 rounded-full">
              {activeImageIndex + 1}/{galleryImages.length}
            </span>
          </div>
        )}

        {/* Dots indicator */}
        {galleryImages.length > 1 && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5">
            {galleryImages.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setActiveImageIndex(idx)}
                className={`w-2 h-2 rounded-full transition-all ${
                  activeImageIndex === idx ? 'bg-white w-4' : 'bg-white/50 hover:bg-white/80'
                }`}
              />
            ))}
          </div>
        )}
      </div>

      {/* 2. ARENA HEADER INFO CARD */}
      <div className="bg-white rounded-3xl p-4 sm:p-5 border border-gray-100 shadow-2xs space-y-2">
        <div className="flex items-center gap-2 flex-wrap">
          {sportCategories.map((sport: any, index: number) => {
            const rawIcon = sport.icon || 'ph:soccer-ball-bold';
            const iconName = rawIcon.includes(':') ? rawIcon : `ph:${rawIcon}`;
            return (
              <span
                key={index}
                className="bg-orange-50 text-[#FC480D] font-extrabold text-xs px-3 py-1 rounded-full border border-orange-100 flex items-center gap-1"
              >
                <Icon icon={iconName} className="w-3.5 h-3.5 text-[#FC480D]" />
                <span>{sport.name || sport}</span>
              </span>
            );
          })}
          <span className="text-xs font-semibold text-gray-500 flex items-center gap-1">
            <Icon icon="ph:clock-bold" className="w-3.5 h-3.5 text-gray-400" />
            Buka {formatOpeningHours(arena.opening_hours)}
          </span>
        </div>

        <h1 className="text-xl font-extrabold text-[#0E1726] leading-snug">{arena.name}</h1>

        <div className="flex items-center gap-1 text-xs text-gray-400 font-semibold truncate">
          <Icon icon="ph:map-pin-bold" className="w-3.5 h-3.5 text-gray-400 shrink-0" />
          <span>{arena.maps?.city || '-'}</span>
        </div>
      </div>

      {/* 3. STEP 1: PILIH LAPANGAN & JADWAL TERSEDIA */}
      <div className="space-y-3.5">
        <div className="flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-[#FC480D] text-white text-xs font-extrabold flex items-center justify-center shrink-0">
            1
          </span>
          <h2 className="text-base font-extrabold text-[#0E1726]">Pilih lapangan & jadwal</h2>
        </div>

        {/* Date Selector Carousel */}
        <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-none">
          {Array.from({ length: 3 }, (_, i) => {
            const date = new Date(selectorBaseDate);
            date.setDate(date.getDate() + i);
            const formattedDate = formatDateToYMD(date);
            const isSelected = selectedDate === formattedDate;
            const dayNames = ['Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu', 'Minggu'];
            const monthNames = [
              'Jan',
              'Feb',
              'Mar',
              'Apr',
              'Mei',
              'Jun',
              'Jul',
              'Aug',
              'Sep',
              'Okt',
              'Nov',
              'Des',
            ];
            const dayName = dayNames[date.getDay() === 0 ? 6 : date.getDay() - 1];

            return (
              <button
                key={i}
                type="button"
                onClick={() => {
                  setSelectedDate(formattedDate);
                  setSelectedTimeSlots([]);
                }}
                className={`flex-1 min-w-[58px] py-2.5 px-2 rounded-2xl border text-center transition-all cursor-pointer ${
                  isSelected
                    ? 'border-2 border-[#FC480D] bg-orange-50/60 text-[#FC480D] font-extrabold shadow-2xs'
                    : 'border-gray-200 bg-white text-gray-700 font-bold hover:border-gray-300'
                }`}
              >
                <div className={`text-[10px] tracking-wider uppercase ${isSelected ? 'text-[#FC480D]' : 'text-gray-400'}`}>
                  {dayName}
                </div>
                <div className="text-base font-extrabold leading-tight mt-0.5">
                  {date.getDate()} {monthNames[date.getMonth()]}
                </div>
              </button>
            );
          })}
          {/* Calendar icon button (matches FieldDetailPage.tsx L1460-1465) */}
          <button
            type="button"
            onClick={() => setIsCalendarModalOpen(true)}
            className="w-12 h-[58px] rounded-2xl border border-gray-200 bg-white flex items-center justify-center text-gray-600 hover:border-gray-300 active:bg-gray-50 shrink-0 cursor-pointer"
            title="Pilih Tanggal Lain via Kalender"
          >
            <Icon icon="mdi:calendar" className="w-6 h-6 text-gray-600" />
          </button>
        </div>

        {/* List Fields Accordion */}
        <div className="space-y-3">
          {physicalFields.map((fieldItem: any, idx: number) => {
            const isSelected = selectedFieldIndex === idx;
            const availableSlots = getAvailableSlotsForField(fieldItem);
            const availableCount = availableSlots.length;

            const fieldType = fieldItem.type || (fieldItem.field_category?.name ? `${fieldItem.field_category.name} · ${fieldItem.type || 'Outdoor'}` : 'Beton · Outdoor');

            return (
              <div
                key={fieldItem.slug || idx}
                className={`bg-white rounded-3xl border transition-all overflow-hidden ${
                  isSelected ? 'border-orange-200 shadow-xs' : 'border-gray-100 shadow-2xs'
                }`}
              >
                {/* Field Header */}
                <div
                  onClick={() => {
                    setSelectedFieldIndex(idx);
                  }}
                  className={`p-4 flex items-center justify-between gap-3 cursor-pointer transition-colors ${
                    isSelected ? 'bg-orange-50/30' : 'hover:bg-gray-50/60'
                  }`}
                >
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="w-10 h-10 rounded-2xl bg-orange-100/60 border border-orange-100 flex items-center justify-center shrink-0">
                      <Icon icon="ph:soccer-ball-bold" className="w-5 h-5 text-[#FC480D]" />
                    </div>

                    <div className="space-y-0.5 min-w-0">
                      <div className="text-sm font-extrabold text-[#0E1726] truncate">
                        {fieldItem.name}
                      </div>
                      <div className="text-xs text-gray-500 font-semibold flex items-center gap-1 truncate">
                        <Icon icon="ph:bounding-box-bold" className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                        <span>{fieldType}</span>
                      </div>
                      <div
                        className={`text-xs font-extrabold ${
                          availableCount > 0 ? 'text-[#FC480D]' : 'text-gray-400'
                        }`}
                      >
                        {availableCount} Jadwal tersedia
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    <Icon
                      icon={isSelected ? 'ph:caret-up-bold' : 'ph:caret-down-bold'}
                      className={`w-5 h-5 transition-transform ${
                        isSelected ? 'text-[#FC480D]' : 'text-gray-400'
                      }`}
                    />
                  </div>
                </div>

                {/* Expanded Time Slot Grid */}
                {isSelected && (
                  <div className="p-4 pt-1 border-t border-gray-100 space-y-3">
                    {availableCount === 0 ? (
                      <div className="p-4 text-center text-xs font-extrabold text-gray-400 bg-gray-50 rounded-2xl border border-dashed border-gray-200">
                        Tidak ada jadwal tersedia pada tanggal ini
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 gap-2.5">
                        {availableSlots.map((slot: any, sIdx: number) => {
                          const timeKey = slot.start_time?.slice(0, 5)?.replace(':', '.') || `slot_${sIdx}`;
                          const formattedStartTime = slot.start_time ? slot.start_time.slice(0, 5) : '00:00';
                          const formattedEndTime = slot.end_time ? slot.end_time.slice(0, 5) : '01:00';
                          const priceVal = slot.final_price || slot.base_price || fieldPrice;
                          const isSlotSelected = selectedTimeSlots.includes(timeKey);
                          const durationHours = Math.max(1, Math.round((slot.duration_minutes || 60) / 60));

                          return (
                            <div
                              key={slot.public_id || sIdx}
                              onClick={() => toggleTimeSlot(timeKey)}
                              className={`p-3 rounded-2xl border transition-all cursor-pointer relative flex flex-col justify-between h-[86px] ${
                                isSlotSelected
                                  ? 'border-2 border-[#FC480D] bg-orange-50/50 shadow-2xs'
                                  : 'border-gray-200 bg-white hover:border-orange-200 active:scale-98'
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <span className="text-[11px] font-extrabold text-gray-400">
                                  {durationHours} Jam
                                </span>
                                <div
                                  className={`w-4 h-4 rounded-full border flex items-center justify-center transition-all ${
                                    isSlotSelected
                                      ? 'border-[#FC480D] bg-[#FC480D]'
                                      : 'border-gray-300 bg-white'
                                  }`}
                                >
                                  {isSlotSelected && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                                </div>
                              </div>

                              <div className="text-sm font-extrabold text-[#0E1726]">
                                {formattedStartTime}-{formattedEndTime}
                              </div>

                              <div className="text-xs font-bold text-gray-600">
                                Rp. {priceVal.toLocaleString('id-ID')}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* 5. FASILITAS ARENA */}
      <div className="space-y-3 pt-2">
        <h2 className="text-base font-extrabold text-[#0E1726]">Fasilitas</h2>
        <div className="bg-white rounded-3xl p-4 sm:p-5 border border-gray-100 shadow-2xs grid grid-cols-2 gap-y-3.5 gap-x-2">
          {displayFacilities.map((fac: any, idx: number) => (
            <div key={idx} className="flex items-center gap-2.5 text-xs font-semibold text-[#0E1726]">
              <Icon icon={fac.icon} className="w-4 h-4 text-[#FC480D] shrink-0" />
              <span className="truncate">{fac.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* 6. LOKASI ARENA */}
      <div className="space-y-3 pt-2">
        <h2 className="text-base font-extrabold text-[#0E1726]">Lokasi</h2>
        <div className="space-y-2.5">
          <div className="flex items-start gap-2 text-xs text-gray-500 font-medium">
            <Icon icon="ph:map-pin-bold" className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" />
            <span>
              {arena.maps?.address ||
                'Jl. Kaliurang Km 5, Sinduadi, Mlati, Kab. Sleman, DI Yogyakarta 55284'}
            </span>
          </div>

          {/* Map Banner Card */}
          <div className="relative w-full h-40 rounded-3xl overflow-hidden bg-gray-200 border border-gray-100 shadow-2xs group">
            <img
              src="https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=800&auto=format&fit=crop&q=80"
              alt="Lokasi Map"
              className="w-full h-full object-cover filter brightness-90 group-hover:scale-105 transition-transform duration-300"
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <button
                type="button"
                onClick={() =>
                  openExternalUrl(
                    arena.maps?.google_maps_url ||
                      'https://maps.google.com/?q=HK+99+Mini+Soccer+Yogyakarta'
                  )
                }
                className="bg-white/95 backdrop-blur-xs text-[#0E1726] text-xs font-extrabold px-5 py-2.5 rounded-full flex items-center gap-2 shadow-lg hover:bg-white transition-all active:scale-98 cursor-pointer"
              >
                <Icon icon="ph:navigation-arrow-bold" className="w-4 h-4 text-[#FC480D]" />
                <span>Buka di Maps</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 7. DESKRIPSI ARENA */}
      <div className="space-y-2 pt-2">
        <h2 className="text-base font-extrabold text-[#0E1726]">Deskripsi</h2>
        <p className="text-xs text-gray-600 leading-relaxed font-medium">
          {arena.description || ''}
        </p>
      </div>

      {/* 8. JAM OPERASIONAL */}
      {arena.opening_hours && (
        <div className="space-y-3 pt-2">
          <h2 className="text-base font-extrabold text-[#0E1726]">Jam Operasional</h2>
          <OpeningHoursComponent openingHours={arena.opening_hours} />
        </div>
      )}

      {/* STICKY BOTTOM ACTION BAR */}
      <div className="fixed bottom-0 left-0 right-0 max-w-md mx-auto bg-white border-t border-gray-100 p-3.5 px-4 z-40 flex items-center justify-between gap-3 shadow-lg">
        <div className="space-y-0.5">
          <div className="text-[11px] font-semibold text-gray-400">
            {selectedTimeSlots.length > 0
              ? `${selectedFieldName} · ${dateLabel} · ${selectedTimeSlots.length} jam`
              : 'Pilih jam untuk lanjut'}
          </div>
          <div
            className={`text-lg font-extrabold ${
              selectedTimeSlots.length > 0 ? 'text-[#FC480D]' : 'text-[#0E1726]'
            }`}
          >
            Rp. {(totalPrice || fieldPrice).toLocaleString('id-ID')}
            <span className="text-xs font-semibold text-gray-400">
              {selectedTimeSlots.length > 0 ? '' : '/jam'}
            </span>
          </div>
        </div>

        <button
          type="button"
          disabled={!isBookingReady}
          onClick={handleBooking}
          className={`h-12 px-6 rounded-full text-xs font-extrabold flex items-center justify-center gap-2 transition-all shadow-md active:scale-98 ${
            isBookingReady
              ? 'bg-[#FC480D] hover:bg-[#D83B08] text-white shadow-orange-500/20 cursor-pointer'
              : 'bg-[#D1D5DB] text-white cursor-not-allowed shadow-none'
          }`}
        >
          <Icon icon="ph:calendar-check-bold" className="w-4 h-4" />
          <span>Booking Sekarang</span>
        </button>
      </div>

      {/* Calendar Modal Component */}
      <CalendarModal
        isOpen={isCalendarModalOpen}
        onClose={() => setIsCalendarModalOpen(false)}
        onSelectDate={handleDateSelect}
        selectedDate={selectedDate}
      />
    </div>
  );
};
